import Vue from 'vue';
import Vuex from 'vuex';
import { round } from 'lodash';

import { GameMode } from '@blackjacktrainer/blackjack-simulator';
import { environment } from '@/constants';
import setupAxios from '@/store/axios';
import setupVuexOrm from '@/store/vuex-orm';
import { deckCounts } from '@/constants';
import User from '@/models/User';

setupAxios(Vue);

Vue.use(Vuex);

export default new Vuex.Store({
  strict: environment !== 'production',

  plugins: [setupVuexOrm(Vue)],

  state: {
    gameStatsVisible: false,

    httpError: null,
    headerContext: { componentName: null },
    settings: {
      hideCardTotals: true,
      // TODO: Make constant `2` DRY with game engine?
      deckCount: 2,
      autoDeclineInsurance: true,
      mode: 'default',
    },

    sessionMovesCorrect: 0,
    sessionMovesTotal: 0,
    focusedHandIndex: 0,
    playCorrection: null,
    step: 'waiting-for-move',

    gameInternalSettings: {},

    shoe: null,
    discardTray: null,
    dealer: {},
    player: {},

    currentUserId: null,
    authenticationEmail: '',
  },

  mutations: {
    setHttpError(state, error) {
      state.httpError = error;
    },

    setHeaderContext(state, headerContext) {
      state.headerContext = headerContext;
    },

    updateGame(state, { attributeName, value }) {
      state[attributeName] = value;
    },

    updateSettings(state, settings) {
      state.settings = Object.assign({}, state.settings, settings);
    },

    updateAuthenticationEmail(state, email) {
      state.authenticationEmail = email;
    },

    setCurrentUserId(state, userId) {
      state.currentUserId = userId;
    },

    toggleGameStatsVisible(state, gameStatsVisible) {
      state.gameStatsVisible = !state.gameStatsVisible;
    },

    setGameStatsVisible(state, value) {
      state.gameStatsVisible = value;
    },

    initializeGame(state, gameSettings) {
      state.gameInternalSettings = gameSettings;
    },
  },

  actions: {
    setHttpError(context, error) {
      context.commit('setHttpError', error);
    },

    setHeaderContext(context, headerContext) {
      context.commit('setHeaderContext', headerContext);
    },

    updateGame(context, { attributeName, value }) {
      context.commit('updateGame', { attributeName, value });
    },

    updateSettings(context, settings) {
      context.commit('updateSettings', settings);
    },

    updateAuthenticationEmail(context, email) {
      context.commit('updateAuthenticationEmail', email);
    },

    clearAuthenticationEmail(context) {
      context.commit('updateAuthenticationEmail', '');
    },

    clickShowStatsButton(context) {
      context.commit('toggleGameStatsVisible');
    },

    clickGameStatsPanel(context) {
      context.commit('setGameStatsVisible', false);
    },

    setupDeviationsGameMode(context) {
      context.commit('setGameStatsVisible', true);
    },

    initializeGame(context, game) {
      context.commit('initializeGame', game);
    },

    async setCurrentUserId({ commit, state }, userId) {
      if (userId && state.userId !== userId) {
        await User.api().get(`/${User.entity}/${userId}`);
      }

      commit('setCurrentUserId', userId);
    },
  },

  getters: {
    deckCountName(state, getters) {
      return deckCounts[getters.settings.deckCount];
    },

    currentUser(state) {
      return User.query().withAllRecursive().find(state.currentUserId);
    },

    settings(state, getters) {
      const settings = Object.assign(
        {},
        state.gameInternalSettings,
        getters.currentUser?.settings ?? state.settings
      );

      // Translate from game module setting to API settings value.
      if (typeof settings.mode === 'number') {
        settings.mode = GameMode[settings.mode].toLowerCase();
      }

      return settings;
    },

    dealerHand(state) {
      return state.dealer.hands?.[0];
    },

    playerHands(state) {
      return state.player?.hands ?? [];
    },

    handWinner(state) {
      return state.player?.handWinner ?? {};
    },

    sessionMovesCorrectPercentage(state) {
      if (state.sessionMovesTotal === 0) {
        return 0;
      }

      return round(
        (state.sessionMovesCorrect / state.sessionMovesTotal) * 100,
        2
      );
    },

    focusedHand(state) {
      return state.player.hands[state.focusedHandIndex];
    },
  },
});
